/**
 * @module cercaliagl/service/Routing
 */

import Service from './Service';
import { KEY, LANG } from '../globals';
import ReverseGeocoding from './ReverseGeocoding';
import LonLat from '../LonLat';
import { assert } from '../AssertionError';
import { i18n } from '../dictionary';

/**
 * @typedef {'basic' | 'detailed' | 'tollGuru'} TollInfoType
 */

/**
 * @typedef {Object} RoutingStep
 * @property {string} [streetId] Street ID. <br/>
 * For example: Avinguda Diagonal (Barcelona) => ESP080193000092155
 * @property {number} [streetNum] House number
 * @property {string} [cityId] City ID. <br/>
 * For example: Alcalá de Henares (Madrid) => ESP17240205552994
 * @property {string} [municipalityId] Municipality ID.
 * P.e: Madrid => ESP280796
 * @property {RoutingStepCoordinates} [coordinates] Step coordinates
 */

/**
 * @typedef {Object} RoutingStepCoordinates
 * @property {LonLat} lonLat
 * @property {boolean} [keepDirection]
 */

/**
 * @typedef {Object} RoutingOptions
 * @property {LonLat|RoutingStep} [destination] Destination coordinates.
 * @property {boolean} [edges] Get edges IDs
 * @property {boolean} [infoedges]  Adds specific information for each section  to the route report. <br/>
 *   An infoedges tag is added to each substage with specific information of each section. <br/>
 *   The following attributes are reported for each section: <br/>
 *   height: height from sea level in meters. <br/>
 *   gradient: slope of the section <br/>
 *   geometry: Stretch geometry  <br/>
 *   meters: Length of the section <br/>
 *   maxkph: Legal maximum speed.
 * @property {string} [lang] Report language (only if report parameter is `true`).
 * @property {boolean} [mindist] Minimum substage distance, in meters. The larger this distance , the smaller the number of substages. Default value `1000`.
 * @property {LonLat|RoutingStep} [origin] Origin coordinates.
 * @property {boolean} [reorder] Specify if you want to reorder or not the route stops, to get the faster/shorter route. Maximum 10 stop points.
 * @property {boolean} [report] Get the textual route report (if not, gets a faster response). Default `0`.
 * @property {Array.<LonLat>|Array.<RoutingStep>} [steps] Array with stop points coordinates.
 * @property {number} [tolerance] Route geometry generalization, in meters (if =0, returns the original geometry).
 * Default value `5` (meters).
 * @property {boolean} [toll] Specify if you need to get the toll roads cost for the route.
 * Currently only avaliable for Spain, Portugal and Andorra. Default `false`.
 * @property {string} [weight] Route type: `time` (faster), `distance` (shorter), `money` (toll free route). Default value: `time`.
 * @property {number} [vweight] Route logistics parameter. Total maximum vehicle weight.
 * @property {number} [vaxleweight] Route logistics parameter. Maximum vehicle axle weight.
 * @property {number} [vheight] Route logistics parameter. Maximum vehicle height.
 * @property {number} [vlength] Route logistics parameter. Maximum vehicle length.
 * @property {number} [vwidth] Route logistics parameter. Maximum vehicle width.
 * @property {number} [vmaxvel] Route logistics parameter. Maximum vehicle speed.
 * @property {boolean} [rimp] Route logistics parameter. Dangerous goods
 * @property {string} [trinfodate] Route logistics parameter. Number days to consecutives to show restrictions. Info date format `YYYYMMDD`.
 * @property {number} [trinfodays] Route logistics parameter. Avoid route restrictions. In timestamp format.
 * @property {number} [trstart] Route logistics parameter. Avoid route restrictions. In timestamp format.
 * @property {boolean} [originIsDepot] This parameter only works if REORDER parameter is used. Values: <br/>
 *  `true` (default): origin cannot be reordered (default). <br/>
 *  `false`: origin is one more stop and can be reordered. Useful when you cannot choose the starting point of the vehicle.
 * @property {boolean} [destinationIsDepot] This parameter only works if REORDER parameter is used. Values: <br/>
 * `true` (default): Destination cannot be reordered (default).<br/>
 * `false`: Destination is one more stop and can be reordered. Useful when after attending some services you don’t have to return to a base.
 * @property {boolean|undefined} [concurrency] Default `true`. Multiple requests at same time. If `false` it cancel previous pending requests.
 * @property {string} [departuretime] Departure time in format ISO8601.
 * @property {TollInfoType} [tollInfoType] Format of the toll information shown when calculating a route. Default value `basic`.
 * @property {string} [tollCurrency] Currency of the toll information displayed when calculating a route. Default value `undefined`.
 * @property {string} [net] Custom routing net. Default value `undefined`.
 */

/**
 * @type {Object.<string, number>}
 */
const Timeouts = {
  DEFAULT: 60000,
  LOGISTICS: 60000,
};

/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const routingService = new cercaliagl.service.Routing({ ... });
 * ```
 *
 * Routing class calculate routes between two or more points. It make calls against Cercalia WS (https://www.cercalia.com/Documentacion/webservices/calculo-de-rutas).<br/>
 * There are many options to calculate route, below in constructor options those are listed.<br/>
 * This class can calculate logistics route for vans and trucks.
 *
 * Example usage:
 *
 * ```js
 * const routingService = new cercaliagl.service.Routing({
 *   origin: new cercaliagl.LonLat(-3.712916732, 40.440202761),
 *   destination: new cercaliagl.LonLat(-3.669923363, 40.443071692)
 *   weight: 'time'
 * });
 *
 * routingService.calculateRoute((data) => {
 *   // Draw feature with route.
 *   if (data.cercalia.route.stages && data.cercalia.route.stages.stage) {
 *     const wkt = data.cercalia.route.stages.stage[0].wkt.value
 *     const feature = new cercaliagl.Feature({wkt: wkt});
 *     map.addFeature(feature);
 *     map.centerToFeatures([feature]);
 *   }
 * });
 * ```
 *
 * @extends {Service}
 * @api
 */
class Routing extends Service {
  /**
   * @constructor
   * @param {RoutingOptions=} opt_options Options
   */
  constructor(options = {}) {
    super({ concurrency: options.concurrency });

    /**
     * @private
     * @type {string}
     */
    this.DEFAULT_WEIGHT_ = 'time';

    /**
     * @private
     * @type {LonLat|RoutingStep|null}
     */
    this.origin_ = options.origin ? options.origin : null;

    /**
     * @private
     * @type {LonLat|RoutingStep|null}
     */
    this.destination_ = options.destination ? options.destination : null;

    /**
     * @private
     * @type {Array.<LonLat|RoutingStep>|null}
     */
    this.steps_ = options.steps ? options.steps : [];

    /**
     * @private
     * @type {string}
     */
    this.weight_ = options.weight ? options.weight : this.DEFAULT_WEIGHT_;

    /**
     * @private
     * @type {boolean}
     */
    this.report_ = !!options.report;

    /**
     * @private
     * @type {string|null}
     */
    this.lang_ = options.lang ? options.lang : LANG;

    /**
     * @private
     * @type {number}
     */
    this.tolerance_ = options.tolerance ? options.tolerance : 5;

    /**
     * @private
     * @type {boolean}
     */
    this.edges_ = !!options.edges;

    /**
     * @private
     * @type {boolean}
     */
    this.infoedges_ = options.infoedges !== undefined ? options.infoedges : true;

    /**
     * @private
     * @type {Array.<string>}
     */
    this.poicats_ = options.poicats ? options.poicats : [];

    /**
     * @private
     * @type {boolean}
     */
    this.reorder_ = !!options.reorder;

    /**
     * @private
     * @type {boolean}
     */
    this.toll_ = !!options.toll;

    /**
     * @private
     * @type {number}
     */
    this.mindist_ = options.mindist ? options.mindist : 1000;

    /**
     * @private
     * @type {string}
     */
    this.intollX_ = options.intoll_x ? options.intoll_x : '1';

    /**
     * @private
     * @type {string}
     */
    this.departuretime_ = options.departuretime ? options.departuretime : null;

    /**
     * @private
     * @type {boolean}
     */
    this.showBreaktimes_ = options.showBreaktimes ? true : null;

    /**
     * @private
     * @type {boolean}
     */
    this.twoDrivers_ = options.twoDrivers ? true : null;

    /**
     * @private
     * @type {Object}
     */
    this.response_ = null;

    /**
     * @private
     * @type {Array.<import('../servers').RoutingResultRoute>|null}
     */
    this.routes_ = null;

    /**
     * @private
     * @type {Object|null}
     */
    this.params_ = null;

    // Logistics Params

    /**
     * @private
     * @type {boolean}
     */
    this.isLogistics_ = options.isLogistics ? options.isLogistics : false;

    /**
     * @private
     * @type {number|null}
     */
    this.vweight_ = options.vweight ? options.vweight : null;

    /**
     * @private
     * @type {number|null}
     */
    this.vaxleweight_ = options.vaxleweight ? options.vaxleweight : null;

    /**
     * @private
     * @type {number|null}
     */
    this.vheight_ = options.vheight ? options.vheight : null;

    /**
     * @private
     * @type {number|null}
     */
    this.vlength_ = options.vlength ? options.vlength : null;

    /**
     * @private
     * @type {number|null}
     */
    this.vwidth_ = options.vwidth ? options.vwidth : null;

    /**
     * @private
     * @type {number|null}
     */
    this.vmaxvel_ = options.vmaxvel ? options.vmaxvel : null;

    /**
     * @private
     * @type {string|undefined}
     */
    this.trinfodate_ = options.trinfodate ? options.trinfodate : null;

    /**
     * @private
     * @type {number|undefined}
     */
    this.trinfodays_ = options.trinfodays ? options.trinfodays : null;

    /**
     * @private
     * @type {number|undefined}
     */
    this.trstart_ = options.trstart ? options.trstart : null;

    /**
     * @private
     * @type {boolean|null}
     */
    this.rimp_ = options.rimp ? options.rimp : null;

    /**
     * @private
     * @type {string|null}
     */
    this.adrTunnel_ = options.adrTunnel ? options.adrTunnel : null;

    /**
     * @private
     * @type {boolean}
     */
    this.originIsDepot_ = options.originIsDepot !== undefined ? options.originIsDepot : true;

    /**
     * @private
     * @type {boolean}
     */
    this.destinationIsDepot_ = options.destinationIsDepot !== undefined ? options.destinationIsDepot : true;

    /**
     * @private
     * @type {boolean}
     */
    this.alternatives_ = options.alternatives !== undefined ? options.alternatives : true;

    /**
     * @private
     * @type {import('../service/ReverseGeocoding').default}
     */
    this.reverseGeocoding_ = new ReverseGeocoding();

    /**
     * @private
     * @type {TollInfoType}
     */
    this.tollInfoType_ = options.tollInfoType ? options.tollInfoType : 'basic';

    /**
     * @private
     * @type {TollInfoType}
     */
    this.tollCurrency_ = options.tollCurrency ? options.tollCurrency : undefined;

    /**
     * @private
     * @type {string}
     */
    this.net_ = options.net ? options.net : undefined;
  }

  /**
   * Set route origin
   * @param {LonLat|RoutingStep} origin Origin
   * @api
   */
  setOrigin(origin) {
    if (origin instanceof LonLat) {
      this.origin_ = origin;
    } else if (origin instanceof Object) {
      this.origin_ = origin;
    } else {
      assert(false, i18n('assertUknownParameter'), origin);
    }
  }

  /**
   * Get route origin position
   * @return {LonLat|RoutingStep} lonLat Origin
   * @api
   */
  getOrigin() {
    return this.origin_;
  }

  /**
   * @param {string} lang Language
  */
  setLang(lang) {
    this.lang_ = lang;
  }

  /**
   * Set route destination
   * @param {LonLat|RoutingStep} destination Destination
   * @api
   */
  setDestination(destination) {
    if (destination instanceof LonLat) {
      this.destination_ = destination;
    } else if (destination instanceof Object) {
      this.destination_ = destination;
    } else {
      assert(false, i18n('assertUknownParameter'), destination);
    }
  }

  /**
   * Get route destination position
   * @return {LonLat|RoutingStep} lonLat Destination
   * @api
   */
  getDestination() {
    return this.destination_;
  }

  /**
   * Add step (or waypoint).
   * @param {LonLat|RoutingStep} step Step.
   * @api
   */
  addStep(step) {
    this.steps_.push(step);
  }

  /**
   * Set steps passing an array.
   * @param {Array.<LonLat|RoutingStep>} steps Steps.
   * @api
   */
  setSteps(steps) {
    this.steps_ = steps;
  }

  /**
   * Get all steps
   * @return {Array.<LonLat|RoutingStep>|Array.<LonLat>|Array.<RoutingStep>} Steps
   * @api
   */
  getSteps() {
    return this.steps_;
  }

  /**
   * Return number of way points.
   * @return {number} Number of way points.
   * @api
   */
  getNumSteps() {
    return this.steps_.length;
  }

  /**
   * Enable route report.
   * @param {boolean} reporting Boolean to enable route report. (First need to calculate route).
   * @api
   */
  setReporting(reporting) {
    this.report_ = reporting;
  }

  /**
   * @return {boolean} report `true` if report is enabled. Else `false`.
   * @api
   */
  isReporting() {
    return !!this.report_;
  }

  /**
   * Route tolerance
   * @param {number} tolerance Tolerance
   * @api
   */
  setTolerance(tolerance) {
    this.tolerance_ = tolerance || 5;
  }

  /**
   * @return {number} tolerance
   * @api
   */
  getTolerance() {
    return this.tolerance_;
  }

  /**
   * Set to get edges from route calculation.
   * @param {number} edges Edges options enabled `true`. Else `false`.
   * @api
   */
  setEdges(edges) {
    this.edges_ = !!edges;
  }

  /**
   * @return {boolean} edges `true` or `false` if edges option is enabled.
   * @api
   */
  getEdges() {
    return this.edges_;
  }

  /**
   * Adds specific information for each section  to the route report. <br/>
   * An infoedges tag is added to each substage with specific information of each section. <br/>
   * The following attributes are reported for each section: <br/>
   * `height`: height from sea level in meters. <br/>
   * `gradient`: slope of the section <br/>
   * `geometry`: Stretch geometry  <br/>
   * `meters`: Length of the section <br/>
   * `maxkph`: Legal maximum speed
   * @param {boolean} infoedges Enable `true`. Else `false`.
   * @api
   */
  setInfoedges(infoedges) {
    this.infoedges_ = infoedges;
  }

  /**
   * @return {boolean} edges `true` or `false` if edges option is enabled.
   * @api
   */
  getInfoedges() {
    return this.infoedges_;
  }

  /**
   * @param {Array.<string>} poicats Array of POI categories.
   * @api
   */
  setPoicats(poicats) {
    this.poicats_ = poicats;
  }

  /**
   * @return {Array.<string>} poicats Array of POI categories.
   * @api
   */
  getPoicats() {
    return this.poicats_;
  }

  /**
   * Enable calculation route reorder points in calculation.
   * @param {boolean} reorder `true` or `false`.
   * @api
   */
  setReorder(reorder) {
    this.reorder_ = reorder;
  }

  /**
   * Get if reorder options to calculate route is enabled.
   * @return {boolean} Reorder `true` or `false`.
   * @api
   */
  isReorder() {
    return this.reorder_;
  }

  /**
   * Enable tolls in route calculation.
   * @param {boolean} toll `true` or `false`.
   * @api
   */
  setToll(toll) {
    this.toll_ = toll;
  }

  /**
   * Get if toll option is enabled.
   * @return {boolean} Toll is `true` or `false`.
   * @api
   */
  isToll() {
    return this.toll_;
  }

  /**
   * @param {string} intoll_x Calculating route using roads with tolls.
   * @api
   */
  setTollX(intollX) {
    this.intoll_x_ = intollX;
  }

  /**
   * @return {string} intoll_x Get ifalculating route using roads with tolls is enabled.
   * @api
   */
  getTollX() {
    return this.intollX_;
  }

  /**
   * @param {boolean} showBreakTimes Show break times.
   * @api
  */
  setShowBreaktimes(showBreakTimes) {
    this.showBreaktimes_ = showBreakTimes;
  }

  /**
   * @param {boolean} twoDrivers Two drivers in the vehicle
   * @api
  */
  setTwoDrivers(twoDrivers) {
    this.twoDrivers_ = twoDrivers;
  }

  /**
   * @return {boolean} Show break times.
   * @api
  */
  getShowBreaktimes() {
    return this.showBreaktimes_;
  }

  /**
   * @param {string} departuretime Departuretime. Example: `2019-08-26T11:49+02:00`-
   * @api
   */
  setDeparturetime(departuretime) {
    this.departuretime_ = departuretime;
  }

  /**
   * @return {string} Departuretime
   * @api
   */
  getDeparturetime() {
    return this.departuretime_;
  }

  /**
   * Set minimum distance option.
   * @param {number} mindist Minimum distance
   * @api
   */
  setMindist(mindist) {
    this.mindist_ = mindist;
  }

  /**
   * @return {number} Get minimum distance option.
   * @api
   */
  getMindist() {
    return this.mindist_;
  }

  /**
   * Get route weight `time|realtime|money|distance`
   * @return {string} Route weight
   * @api
   */
  getWeight() {
    return this.weight_;
  }

  /**
   * Set weight to calculate route. Weight values available: `time|realtime|money|distance`
   * @param {string} weight Weight. `time|realtime|money|distance`
   * @api
   */
  setWeight(weight) {
    this.weight_ = weight;
  }

  /**
   * @param {import('../MapRouting').LogisticsParams|undefined} logisticsParams Logistics params.
   * @api
   */
  setLogisticsParams(logisticsParams) {
    logisticsParams = /** @type {import('../MapRouting').LogisticsParams} */ (logisticsParams || {}); // eslint-disable-line no-param-reassign
    this.vweight_ = logisticsParams.vweight ? logisticsParams.vweight : null;
    this.vaxleweight_ = logisticsParams.vaxleweight ? logisticsParams.vaxleweight : null;
    this.vheight_ = logisticsParams.vheight ? logisticsParams.vheight : null;
    this.vlength_ = logisticsParams.vlength ? logisticsParams.vlength : null;
    this.vwidth_ = logisticsParams.vwidth ? logisticsParams.vwidth : null;
    this.vmaxvel_ = logisticsParams.vmaxvel ? logisticsParams.vmaxvel : null;
    this.rimp_ = logisticsParams.rimp ? logisticsParams.rimp : null;
    this.adrTunnel_ = logisticsParams.adrtunnel ? logisticsParams.adrtunnel : null;
    this.trinfodate_ = logisticsParams.trinfodate ? logisticsParams.trinfodate : null;
    this.trinfodays_ = logisticsParams.trinfodays ? logisticsParams.trinfodays : null;
    this.trstart_ = logisticsParams.trstart ? logisticsParams.trstart : null;
  }

  /**
   * Clear all option values to calculate route.
   * @api
   */
  clear() {
    this.origin_ = null;
    this.destination_ = null;
    this.steps_ = [];
    this.weight_ = this.DEFAULT_WEIGHT_;
    this.report_ = false;
    this.tolerance_ = 5;
    this.edges_ = true;
    this.infoedges_ = false;// default
    this.poicats_ = [];
    this.reorder_ = false; // default
    this.toll_ = false; // default
    this.mindist_ = 1000; // default
    this.departuretime_ = null;
    this.originIsDepot_ = true;// default
    this.destinationIsDepot_ = true;// default
    this.response_ = null;
    this.routes_ = null;
  }

  /**
   * Returns `true` if it is ready to calculate. When options has been set. (minimum `origin` and `destination`)
   * @return {boolean} `true` if can calculate route.
   * @api
   */
  isReady() {
    return this.origin_ !== null && this.destination_ !== null;
  }

  /**
   * @private
   */
  prepareParameters_() {
    const weight = this.isLogistics_ && this.weight_ === 'realtime' ? 'time' : this.weight_;
    const params = {
      key: KEY,
      cmd: 'routingstage',
      weight: weight + (this.rimp_ ? 'rimp' : ''),
      adrtunnel: this.rimp_ ? this.adrTunnel_ : undefined,
      tolerance: this.tolerance_,
      edges: this.edges_,
      infoedges: this.infoedges_ ? 1 : 0,
      report: this.report_ ? 1 : 0,
      splitGeom: '1',
      iweight: 'realtime',
      reorder: this.reorder_ ? 1 : undefined,
      originisdepot: this.originIsDepot_ ? 1 : 0,
      destinationisdepot: this.destinationIsDepot_ ? 1 : 0,
      alternatives: this.alternatives_ ? 1 : 0,
      lang: this.lang_,
      mindist: this.report_ && this.mindist_ ? this.mindist_ : undefined,
      toll: this.toll_ ? 1 : 0,
      departuretime: this.departuretime_ ? this.departuretime_ : undefined,
      is_logistics: this.isLogistics_,
      vweight: this.vweight_ ? this.vweight_ : undefined,
      vaxleweight: this.vaxleweight_ ? this.vaxleweight_ : undefined,
      vheight: this.vheight_ ? this.vheight_ : undefined,
      vlength: this.vlength_ ? this.vlength_ : undefined,
      vwidth: this.vwidth_ ? this.vwidth_ : undefined,
      vmaxvel: this.vmaxvel_ ? this.vmaxvel_ : undefined,
      trinfodate: this.trinfodate_ ? this.trinfodate_ : undefined,
      trinfodays: this.trinfodays_ ? this.trinfodays_ : undefined,
      trstart: this.trstart_ ? this.trstart_ : undefined,
      showbreaktimes: this.showBreaktimes_ ? 1 : 0,
      twodrivers: this.showBreaktimes_ && this.twoDrivers_ ? 1 : 0,
      detailedtolls: this.tollInfoType_ === 'detailed' ? 1 : 0,
      premiumtolls: this.tollInfoType_ === 'tollGuru' ? 1 : 0,
      currencytolls: this.tollCurrency_ ? this.tollCurrency_ : undefined,
      net: this.net_ ? this.net_ : undefined,
    };

    // Generamos los parámetros de forma dinámica según los valors de la variables origin/destination/steps

    // Origen
    if (this.origin_ instanceof LonLat) {
      params.mo_o = `${this.origin_.getLat()},${this.origin_.getLon()}|ORIGIN`;
      params.intoll_o = this.intoll_x_; // posem el parametre 'intoll_o'
    } else if (this.origin_.streetId) {
      params.stc_o = this.origin_.streetId;
      if (this.origin_.streetNum) {
        params.stnum_o = this.origin_.streetNum;
      }
    } else if (this.origin_.cityId) {
      params.ctc_o = this.origin_.cityId;
    } else if (this.origin_.municipalityId) {
      params.munc_o = this.origin_.municipalityId;
    } else {
      assert(false, i18n('assertOriginInvalid'), this.origin_);
    }

    // Pasos intermedios (opcional)
    this.steps_.forEach((elem, i) => {
      if (elem instanceof LonLat) {
        const stepId = elem.get('stepId') ? elem.get('stepId') : `Step ${i + 1}`;
        elem.set('stepId', stepId);
        params[`mo_${i + 1}`] = `${elem.getLat()},${elem.getLon()}|${stepId}`;
        params[`intoll_${i + 1}`] = this.intoll_x_; // posem el parametre 'intoll_x'
      } else if (elem.coordinates) {
        const stepId = `Step ${i + 1}`;
        const { coordinates } = elem;
        params[`${coordinates.keepDirection ? 'by' : 'mo'}_${i + 1}`] = `${coordinates.lonLat.getLat()},${coordinates.lonLat.getLon()}|${stepId}`;
      } else if (elem.streetId) {
        params[`stc_${i + 1}`] = elem.streetId;
        if (elem.streetNum) {
          params[`stnum_${i + 1}`] = elem.streetNum;
        }
      } else if (elem.cityId) {
        params[`ctc_${i + 1}`] = elem.cityId;
      } else if (elem.municipalityId) {
        params[`munc_${i + 1}`] = elem.municipalityId;
      } else {
        assert(false, `${i18n('assertStepInvalid')} (step ${i})`, elem);
      }
    });

    // Destination
    if (this.destination_ instanceof LonLat) {
      params.mo_d = `${this.destination_.getLat()},${this.destination_.getLon()}|DESTINATION`;
      params.intoll_d = this.intoll_x_; // posem el parametre 'intoll_d'
    } else if (this.destination_.streetId) {
      params.stc_d = this.destination_.streetId;
      if (this.destination_.streetNum) {
        params.stnum_d = this.destination_.streetNum;
      }
    } else if (this.destination_.cityId) {
      params.ctc_d = this.destination_.cityId;
    } else if (this.destination_.municipalityId) {
      params.munc_d = this.destination_.municipalityId;
    } else {
      assert(false, i18n('assertDestinationInvalid'), this.destination_);
      return;
    }

    this.params_ = params;
  }

  /**
   * Calcula la ruta especificando una funcion de callback que se ejecuta al volver el resultado
   * @param {Function} callbackFn Callback Function after calculate route
   * @param {Function} [callbackErrorFn] Callback Error Function after calculate route error.
   * @api
   */
  calculateRoute(callbackFn, callbackErrorFn) {
    this.prepareParameters_();
    this.calculateRouteInner_(callbackFn, callbackErrorFn);
  }

  /**
   * Recalculate routing with same parameters.
   * @param {Function} callbackFn Callback Function after calculate route
   * @param {Function=} [callbackErrorFn] Callback Error Function after calculate route error.
   */
  calculateReport(callbackFn, callbackErrorFn) {
    const oldMindistVal = this.params_.mindist;
    const oldReportVal = this.params_.report ? this.params_.report : 0;
    this.prepareParameters_();
    this.params_.report = 1;
    this.params_.mindist = 0;
    this.calculateRouteInner_(callbackFn, callbackErrorFn);
    this.params_.report = oldReportVal;
    this.params_.mindist = oldMindistVal;
  }

  /**
   * @private
   * @param {Function} callbackFn Callback Function after calculate route
   * @param {Function=} opt_callbackErrorFn Callback Error Function after calculate route error.
   */
  calculateRouteInner_(callbackFn, callbackErrorFn) {
    const timeout = !this.isLogistics_ ? Timeouts.DEFAULT : Timeouts.LOGISTICS;
    this.getRequest(/** @type {import('./Service').AjaxParams} */({
      data: this.params_,
      timeout,
      callbackFn: (data) => {
        this.response_ = data;

        if (data.cercalia.error) {
          const { error } = data.cercalia;
          if (callbackErrorFn) {
            callbackErrorFn(data);
          } else {
            throw new Error(`Cercalia Error:${error.id}, type_id:${error.type}, value:${error.value}`);
          }
        } else {
          this.routes_ = Array.isArray(data.cercalia.route) ? data.cercalia.route : [data.cercalia.route];
          callbackFn(data);
        }
      },
      callbackFnError: (data) => {
        if (callbackErrorFn) {
          callbackErrorFn(data);
        } else {
          console.error('Uncaught exception', data); // eslint-disable-line no-console
        }
      },
    }));
  }

  /**
   * Obtiene WKT de la ruta calculada
   * @param {number} [routeIndex] Route index alternative.
   * @return {string|null} WKT de la ruta calculada. Devuelve null si no se ha calculado ninguna
   * @api
   */
  getWKT(routeIndex = 0) {
    if (!this.routes_ || !this.routes_[routeIndex] || !this.routes_[routeIndex].stages) {
      return null;
    }
    return Routing.wktfromStages(this.routes_[routeIndex].stages.stage);
  }

  /**
   * Obtiene `stages`.
   * @param {number} [routeIndex] Route index alternative.
   * @return {Array.<import('../servers').RoutingResultStages>|null} WKT de la ruta calculada. Devuelve null si no se ha calculado ninguna
   * @api
   */
  getStages(routeIndex = 0) {
    return this.routes_[routeIndex].stages.stage;
  }

  /**
   * Get response
   * @return {Object} response
   * @api
   */
  getResponse() {
    return this.response_;
  }

  /**
   * @return {Array.<import('../servers').RoutingResultRoute>|null}
   * @api
   */
  getRoutes() {
    return this.routes_;
  }

  /**
   * Get Route Id (internal id from Cercalia servers)
   * @param {number} [routeIndex] Route index alternative.
   * @return {string|null} Cercalia route id
   * @api
   */
  getRouteId(routeIndex = 0) {
    if (this.response_ && this.routes_ && this.routes_[routeIndex]) {
      return this.routes_.id;
    }
    return null;
  }

  /**
   * @api
   * @param {boolean} isLogistics Enable logistic route.
   */
  setLogistics(isLogistics) {
    this.isLogistics_ = isLogistics;
  }

  /**
   * @api
   * @return {boolean} `true` if route has logistics parameters.
   */
  isLogistics() {
    return this.isLogistics_;
  }

  /**
   * @api
   * @param {boolean} originIsDepot Enable `originisdepot` parameter
   */
  setOriginIsDepot(originIsDepot) {
    this.originIsDepot_ = originIsDepot;
  }

  /**
   * @api
   * @return {boolean} Get `originisdepot` parameter value.
   */
  getOriginIsDepot() {
    return this.originIsDepot_;
  }

  /**
   * @api
   * @param {boolean} destinationIsDepot Enable `destinationisdepot` parameter
   */
  setDestinationIsDepot(destinationIsDepot) {
    this.destinationIsDepot_ = destinationIsDepot;
  }

  /**
   * @api
   * @return {boolean} Get `destinationisdepot` parameter value.
   */
  getDestinationIsDepot() {
    return this.destinationIsDepot_;
  }

  /**
   * Enable alternatives routing calculation
   * @api
   * @param {boolean} destinationIsDepot Enable `alternatives` flag
   */
  setAlternatives(alternatives) {
    this.alternatives_ = alternatives;
  }

  /**
   * @api
   * @return {boolean} Return if `alternatives` flag is enabled
   */
  isAlternatives() {
    return this.alternatives_;
  }

  /**
   * @private
   * @param {Array.<import('../servers').RoutingResultStages>} stages Stages.
   * @return {string} Geometry in WKT format.
   */
  static wktfromStages(stages) {
    let wkt = '';

    for (let i = 0; i < stages.length; i += 1) {
      const route = stages[i];
      if (route.wkt && route.wkt.value) {
        const m = route.wkt.value.match(/\([^\(\)]+\)/g); // eslint-disable-line no-useless-escape
        if (m) {
          for (let j = 0; j < m.length; j += 1) {
            let mWKT = '';
            if (wkt !== '') {
              mWKT = ',';
            }
            mWKT += m[j];
            wkt += mWKT;
          }
        }
      }
    }

    let wktStr;

    if (stages.length === 1) {
      wktStr = `LINESTRING${wkt}`;
    } else {
      wktStr = `MULTILINESTRING(${wkt})`;
    }
    return wktStr;
  }
}

export default Routing;
